import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText, FileDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  exportToExcel,
  exportToCSV,
  exportToPDF,
  ExportColumn,
} from '@/lib/exportUtils';

interface ExportButtonProps {
  data: Record<string, any>[] | undefined;
  columns: ExportColumn[];
  filename: string;
  title?: string;
  subtitle?: string;
  dateRange?: { start: Date; end: Date };
  disabled?: boolean;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export function ExportButton({
  data,
  columns,
  filename,
  title,
  subtitle,
  dateRange,
  disabled,
  variant = 'outline',
  size = 'default',
}: ExportButtonProps) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format: 'excel' | 'csv' | 'pdf') => {
    if (!data || data.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    setExporting(true);
    try {
      const options = {
        filename,
        title,
        subtitle,
        columns,
        data,
        dateRange,
      };

      switch (format) {
        case 'excel':
          exportToExcel(options);
          break;
        case 'csv':
          exportToCSV(options);
          break;
        case 'pdf':
          exportToPDF(options);
          break;
      }

      toast.success(`${data.length} registros exportados correctamente`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar los datos');
    } finally {
      setExporting(false);
    }
  };

  const isDisabled = disabled || exporting || !data || data.length === 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} disabled={isDisabled}>
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => handleExport('excel')}>
          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
          Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('csv')}>
          <FileText className="w-4 h-4 mr-2 text-blue-600" />
          CSV (.csv)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleExport('pdf')}>
          <FileDown className="w-4 h-4 mr-2 text-red-600" />
          PDF (.pdf)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ExportButton;
